import clsx from 'clsx';
import { useRef, useState, useEffect } from 'react';

import {
  IconCalendarDue,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react';

import { useFormatText } from '@/hooks/useFormatText';

import Button from '../Button/Button';

interface IDateRangePickerProps {
  placeholder?: string;
  setFirstDate?: React.Dispatch<React.SetStateAction<string | null>>;
  setSecondDate?: React.Dispatch<React.SetStateAction<string | null>>;
  className?: string;
  popUpClassName?: string;
  popupAlign?: 'left' | 'right';
  dataTestId?: string;
}

const DateRangePicker = ({
  placeholder,
  setFirstDate,
  setSecondDate,
  className,
  popUpClassName,
  popupAlign = 'left',
  dataTestId = 'date-range-picker',
}: IDateRangePickerProps) => {
  const normalizeToNoon = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12);

  const formatStartDate = (date: Date | null) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  const formatEndDate = (date: Date | null) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day} 23:59:59`;
  };

  const formatDisplayDate = (date: Date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear());
    return `${day}-${month}-${year}`;
  };

  const ALL_KEY = 'rangePicker.all';
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState<Date | null>(startDate);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(endDate);
  const { formatText } = useFormatText();
  const dropdownOptions = [
    { key: 'all', name: ALL_KEY },
    { key: 'today', name: 'rangePicker.today' },
    { key: 'yesterday', name: 'rangePicker.yesterday' },
    { key: 'this_week', name: 'rangePicker.thisWeek' },
    { key: 'last_week', name: 'rangePicker.lastWeek' },
    { key: 'this_month', name: 'rangePicker.thisMonth' },
    { key: 'last_month', name: 'rangePicker.lastMonth' },
    { key: 'this_year', name: 'rangePicker.thisYear' },
    { key: 'last_year', name: 'rangePicker.lastYear' },
  ];

  const [startViewYear, setStartViewYear] = useState(new Date().getFullYear());
  const [startViewMonth, setStartViewMonth] = useState(new Date().getMonth());
  const [endViewYear, setEndViewYear] = useState(new Date().getFullYear());
  const [endViewMonth, setEndViewMonth] = useState(new Date().getMonth());

  const generateCalendarDays = (year: number, month: number) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const daysInMonth = [];
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const normalizedDate = new Date(year, month, day, 12);
      daysInMonth.push(normalizedDate);
    }

    const leadingDays = firstDay.getDay();
    const trailingDays = 6 - lastDay.getDay();

    return [
      ...Array(leadingDays).fill(null),
      ...daysInMonth,
      ...Array(trailingDays).fill(null),
    ];
  };

  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !dropdownRef.current?.contains(event.target as Node) &&
        !buttonRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleDateClick = (date: Date, type: 'start' | 'end') => {
    const normalizedDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      12
    ); // Normalize time
    if (type === 'start') {
      setTempStartDate(normalizedDate);
    } else {
      setTempEndDate(normalizedDate);
    }
  };

  const handleMonthChange = (
    direction: 'prev' | 'next',
    type: 'start' | 'end'
  ) => {
    const isStart = type === 'start';
    const currentMonth = isStart ? startViewMonth : endViewMonth;
    const currentYear = isStart ? startViewYear : endViewYear;
    const setMonth = isStart ? setStartViewMonth : setEndViewMonth;
    const setYear = isStart ? setStartViewYear : setEndViewYear;

    const newDate = new Date(
      currentYear,
      currentMonth + (direction === 'prev' ? -1 : 1),
      1
    );

    setMonth(newDate.getMonth());
    setYear(newDate.getFullYear());
  };

  const renderCalendar = (type: 'start' | 'end') => {
    const viewYear = type === 'start' ? startViewYear : endViewYear;
    const viewMonth = type === 'start' ? startViewMonth : endViewMonth;
    const calendarDays = generateCalendarDays(viewYear, viewMonth);

    const isDateDisabled = (date: Date | null) => {
      if (!date) return true;
      const today = normalizeToNoon(new Date());

      // Disable dates after today (tomorrow and further), but allow today
      if (date > today) return true;
      if (type === 'end') {
        if (tempStartDate && date < tempStartDate) return true;
      } else if (type === 'start' && tempEndDate && date > tempEndDate) {
        return true;
      }
      return false;
    };

    const isDateHighlighted = (date: Date | null) => {
      if (!date) return false;
      return (
        date.toDateString() ===
        (type === 'start'
          ? tempStartDate?.toDateString()
          : tempEndDate?.toDateString())
      );
    };

    const getDateClasses = (date: Date | null) => {
      if (!date) return 'text-neutral-disabled';

      if (isDateDisabled(date)) {
        return 'bg-neutral-light-50 text-neutral-disabled !cursor-not-allowed';
      }

      if (isDateHighlighted(date)) {
        return 'bg-brand-default text-white';
      }

      if (
        tempStartDate &&
        tempEndDate &&
        date > tempStartDate &&
        date < tempEndDate
      ) {
        return 'bg-brand-light-50 text-neutral-primary';
      }

      return 'hover:border-brand-default hover:border hover:bg-brand-light-50 hover:text-neutral-primary text-neutral-secondary';
    };

    return (
      <div className="flex flex-col items-center p-4 max-[917px]:p-2 gap-3">
        <div className="flex items-center justify-between w-full">
          <button onClick={() => handleMonthChange('prev', type)}>
            <IconChevronLeft
              width={20}
              height={20}
              className="text-neutral-secondary"
            />
          </button>
          <p className="text-sm font-medium text-neutral-primary">
            {new Date(viewYear, viewMonth).toLocaleString('default', {
              month: 'long',
            })}{' '}
            {viewYear}
          </p>
          <button onClick={() => handleMonthChange('next', type)}>
            <IconChevronRight
              width={20}
              height={20}
              className="text-neutral-secondary"
            />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-2 max-[917px]:gap-1 text-center">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
            <p
              key={day}
              className="font-semibold text-xs py-2 text-neutral-primary"
            >
              {day}
            </p>
          ))}
          {calendarDays.map((date, index) => (
            <div
              key={index}
              className={`w-8 h-8 flex items-center justify-center rounded cursor-pointer
                ${getDateClasses(date)}`}
              onClick={() =>
                !isDateDisabled(date) && handleDateClick(date, type)
              }
            >
              {date ? date.getDate() : ''}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const setQuickRange = (option: string) => {
    const today = normalizeToNoon(new Date());
    let start: Date | null = null,
      end: Date | null = null;

    switch (option) {
      case 'all':
        break;
      case 'today':
        start = end = today;
        break;
      case 'yesterday':
        start = new Date(today);
        start.setDate(start.getDate() - 1);
        start = normalizeToNoon(start);
        end = start;
        break;
      case 'this_week':
        start = new Date(today);
        start.setDate(start.getDate() - start.getDay());
        start = normalizeToNoon(start);
        end = new Date(start);
        end.setDate(start.getDate() + 6);
        end = normalizeToNoon(end);
        if (end > today) end = today;
        break;
      case 'this_month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        start = normalizeToNoon(start);
        end = today;
        break;
      case 'this_year':
        start = new Date(today.getFullYear(), 0, 1);
        start = normalizeToNoon(start);
        end = today;
        break;
      case 'last_week':
        start = new Date(today);
        start.setDate(start.getDate() - start.getDay() - 7);
        start = normalizeToNoon(start);
        end = new Date(start);
        end.setDate(start.getDate() + 6);
        end = normalizeToNoon(end);
        break;
      case 'last_month':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        start = normalizeToNoon(start);
        end = normalizeToNoon(end);
        break;
      case 'last_year':
        start = new Date(today.getFullYear() - 1, 0, 1);
        end = new Date(today.getFullYear() - 1, 11, 31);
        start = normalizeToNoon(start);
        end = normalizeToNoon(end);
        break;
      /* istanbul ignore next */
      default:
        break;
    }

    const firstDate = formatStartDate(start);
    const secondDate = formatEndDate(end);

    if (setFirstDate) setFirstDate(firstDate);
    if (setSecondDate) setSecondDate(secondDate);

    setTempStartDate(start);
    setTempEndDate(end ?? null);
    setStartDate(start);
    setEndDate(end ?? null);
    setIsOpen(false);
  };

  const selectedLabel = (() => {
    if (tempStartDate && tempEndDate) {
      return `${formatDisplayDate(tempStartDate)} - ${formatDisplayDate(tempEndDate)}`;
    }

    if (tempStartDate) return formatDisplayDate(tempStartDate);

    return placeholder ?? formatText(ALL_KEY);
  })();

  return (
    <div className="relative min-w-50 w-full text-nowrap">
      <button
        ref={buttonRef}
        data-testid={dataTestId}
        className={clsx(
          'px-4 py-3.5 flex justify-between items-center gap-2 text-sm text-neutral-primary font-medium border-neutral-light-200 border rounded-lg w-full bg-white shadow-s hover:border-brand-default focus:outline-2 focus:outline-offset-2 focus:border-[#e3ecff] focus:outline-[#e3ecff] cursor-pointer',
          className
        )}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {selectedLabel}
        <IconCalendarDue size={20} className="text-neutral-500" />
      </button>

      {isOpen && (
        <div
          ref={dropdownRef}
          className={clsx(
            'absolute z-40 mt-2 bg-white border border-neutral-light-200 shadow-s rounded-lg flex w-max items-stretch',
            popupAlign === 'right'
              ? 'right-0 rtl:right-auto rtl:left-0'
              : 'left-0 rtl:left-auto rtl:right-0',
            popUpClassName
          )}
        >
          <div className="border-r border-neutral-light-200 p-4">
            <ul className="flex flex-col">
              {dropdownOptions.map((option) => (
                <li key={option.key}>
                  <button
                    className={clsx(
                      'text-sm font-medium text-neutral-primary hover:text-brand-default hover:bg-brand-light-50 w-full text-left px-2 py-1.5 rounded'
                    )}
                    onClick={() => setQuickRange(option.key)}
                  >
                    {formatText(option.name)}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col h-full">
            <div className="flex flex-1">
              <div className="border-r border-neutral-light-200">
                {renderCalendar('start')}
              </div>
              <div>{renderCalendar('end')}</div>
            </div>

            <div className="flex justify-between items-center gap-2 border-t border-neutral-light-200 py-3 px-4 mt-auto">
              <p className="text-base font-medium text-neutral-primary">
                {selectedLabel}
              </p>
              <div className="flex gap-4 items-center">
                <Button
                  label={formatText('common.actions.clear')}
                  variant="link"
                  className="px-4 py-1.5 text-xs font-semibold"
                  onClick={() => {
                    setTempStartDate(null);
                    setTempEndDate(null);
                    setStartDate(null);
                    setEndDate(null);
                    if (setFirstDate) setFirstDate(null);
                    if (setSecondDate) setSecondDate(null);
                    setIsOpen(false);
                  }}
                />
                <Button
                  label={formatText('common.actions.cancel')}
                  variant="outline"
                  className="px-4 py-1.5 text-xs font-semibold"
                  onClick={() => {
                    setTempStartDate(startDate);
                    setTempEndDate(endDate);
                    setIsOpen(false);
                  }}
                />
                <Button
                  className="px-3.5! py-2.5!"
                  onClick={() => {
                    const first = formatStartDate(tempStartDate);
                    const second = formatEndDate(tempEndDate);
                    if (setFirstDate) setFirstDate(first);
                    if (setSecondDate) setSecondDate(second);
                    setStartDate(tempStartDate);
                    setEndDate(tempEndDate);
                    setIsOpen(false);
                  }}
                  label={formatText('common.actions.apply')}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
